import { CheckIcon } from '@app/features/analytics/icons/check';
import { Listbox, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';

import { CaretDown } from '../icons/caret-down';
import { Spinner } from '../icons/spinner';
import { Tooltip } from '../tooltip';

export interface SelectOption<T> {
  value: T;
  label: string | ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  tooltip?: ReactNode;
}

interface BaseSelectProps<T> {
  options: SelectOption<T>[];
  label: string | ReactNode;
  className?: string;
  buttonClassName?: string;
  renderOption?: (
    option: SelectOption<T>,
    selected: boolean,
    active: boolean,
  ) => ReactNode;
  icon?: ReactNode;
  position?: 'left' | 'right';
  inline?: boolean;
  hasErrors?: boolean;
  id?: string;
  accessibleLabel?: string;
  accessibleLabelClassName?: string;
  portal?: boolean;
  disabled?: boolean;
  selectAllValue?: T;
  selectAllLabel?: string | ReactNode;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** Max width of the options panel in px. Defaults to 320. Ignored when `matchTriggerWidth` is true. */
  dropdownMaxWidth?: number;
  /** When true, the options panel width matches the select button width. */
  matchTriggerWidth?: boolean;
  // Called with the dropdown's open state on every transition, and once on
  // mount with `false`.
  onOpenChange?: (open: boolean) => void;
}

type SingleSelectProps<T> = BaseSelectProps<T> & {
  multiple?: false;
  value: T;
  onChange: (value: T) => void;
};

type MultiSelectProps<T> = BaseSelectProps<T> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
};

type SelectProps<T> = SingleSelectProps<T> | MultiSelectProps<T>;

export const Select = <T extends string | number>({
  value,
  onChange,
  options,
  label,
  multiple = false,
  className,
  buttonClassName,
  renderOption,
  icon,
  position = 'right',
  inline = false,
  hasErrors = false,
  id,
  accessibleLabel,
  accessibleLabelClassName,
  portal = false,
  disabled = false,
  selectAllValue,
  selectAllLabel,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  dropdownMaxWidth = 320,
  matchTriggerWidth = false,
  onOpenChange,
}: SelectProps<T>) => {
  const { t } = useTranslation();
  const { ref: loadMoreRef, inView: loadMoreInView } = useInView();

  useEffect(() => {
    if (!loadMoreInView || !hasMore || isLoadingMore) return;
    onLoadMore?.();
  }, [loadMoreInView, hasMore, isLoadingMore, onLoadMore]);

  const selectedCount = Array.isArray(value)
    ? value.includes(selectAllValue as T)
      ? options.length
      : value.length
    : 0;
  const selectedValueSet = useMemo(
    () => new Set(Array.isArray(value) ? value : []),
    [value],
  );
  const renderedOptions = useMemo(
    () =>
      multiple && selectAllValue !== undefined && selectAllLabel !== undefined
        ? [
            { value: selectAllValue, label: selectAllLabel },
            ...options.filter(option => option.value !== selectAllValue),
          ]
        : options,
    [multiple, options, selectAllLabel, selectAllValue],
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [_placement, setPlacement] = useState<'bottom' | 'top'>('bottom');

  const DROPDOWN_MAX_HEIGHT = 320; // max-h-80 = 20rem = 320px
  const DROPDOWN_GAP = 4;

  const updateDropdownPosition = () => {
    if (buttonRef.current && portal) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldFlipToTop =
        spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

      setPlacement(shouldFlipToTop ? 'top' : 'bottom');

      const panelWidth = matchTriggerWidth
        ? rect.width
        : Math.max(rect.width, dropdownMaxWidth);

      setDropdownStyle({
        position: 'fixed',
        ...(shouldFlipToTop
          ? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP }
          : { top: rect.bottom + DROPDOWN_GAP }),
        left: position === 'left' ? rect.left : undefined,
        right:
          position === 'right' ? window.innerWidth - rect.right : undefined,
        minWidth: rect.width,
        width: matchTriggerWidth ? rect.width : undefined,
        // Never let a long option label widen the dropdown past the trigger
        // (or past dropdownMaxWidth for narrow triggers) — labels truncate
        // instead of pushing the panel off screen.
        maxWidth: panelWidth,
        maxHeight: shouldFlipToTop
          ? Math.min(spaceAbove - DROPDOWN_GAP, DROPDOWN_MAX_HEIGHT)
          : Math.min(spaceBelow - DROPDOWN_GAP, DROPDOWN_MAX_HEIGHT),
      });
    }
  };

  const handleChange = useCallback(
    (nextValue: T | T[]) => {
      if (!multiple) {
        (onChange as (value: T) => void)(nextValue as T);
        return;
      }

      const nextValues = nextValue as T[];

      if (selectAllValue === undefined) {
        (onChange as (value: T[]) => void)(nextValues);
        return;
      }

      if (
        nextValues.includes(selectAllValue) &&
        !selectedValueSet.has(selectAllValue)
      ) {
        (onChange as (value: T[]) => void)([selectAllValue]);
        return;
      }

      (onChange as (value: T[]) => void)(
        nextValues.filter(selectedValue => selectedValue !== selectAllValue),
      );
    },
    [multiple, onChange, selectAllValue, selectedValueSet],
  );

  const renderDropdownContent = (open: boolean) => (
    <Transition
      as={Fragment}
      show={open}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Listbox.Options
        onKeyDownCapture={event => {
          if (event.key === 'Escape') {
            // Restore focus before unmount cancels Headless UI's deferred work.
            buttonRef.current?.focus({ preventScroll: true });
          }
        }}
        static={portal}
        style={
          portal
            ? dropdownStyle
            : {
                maxWidth: matchTriggerWidth ? '100%' : dropdownMaxWidth,
                width: matchTriggerWidth ? '100%' : undefined,
              }
        }
        className={cx(
          'min-w-[12.5rem] max-h-80 overflow-auto border border-neutral-b4 rounded-lg',
          'bg-white py-1 text-sm shadow-lg outline-none list-none m-0',
          {
            'absolute z-50 mt-1': !portal,
            'z-[9999]': portal,
            'min-w-full': !inline && !portal,
            'left-0': position === 'left' && !portal,
            'right-0': position === 'right' && !portal,
          },
        )}
      >
        {renderedOptions.map(option => (
          <Listbox.Option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {({ selected, active, disabled: optionDisabled }) => {
              const optionContent = renderOption ? (
                renderOption(option, selected, active)
              ) : (
                <div
                  className={cx(
                    'select-none px-3 py-2 flex items-center justify-between gap-1',
                    {
                      'cursor-pointer': !optionDisabled,
                      'cursor-not-allowed opacity-50': optionDisabled,
                      'bg-white text-black':
                        !selected && !active && !optionDisabled,
                      'bg-blue-100 text-blue-500':
                        (active || selected) && !optionDisabled,
                      'bg-gray-50 text-gray-400': optionDisabled,
                    },
                  )}
                >
                  {option.icon && (
                    <span className="w-5 h-5 shrink-0 flex items-center justify-center">
                      {option.icon}
                    </span>
                  )}
                  {typeof option.label === 'string' ? (
                    <span
                      className="grow min-w-0 truncate"
                      title={option.label}
                    >
                      {option.label}
                    </span>
                  ) : (
                    option.label
                  )}
                  {selected && (
                    <span className="w-5 h-5 shrink-0">
                      <CheckIcon />
                    </span>
                  )}
                </div>
              );

              if (!option.tooltip) {
                return optionContent;
              }

              return (
                <Tooltip
                  tooltip={option.tooltip}
                  placement="right-center"
                  childClassName="block w-full"
                  className="!z-[10000]"
                >
                  {optionContent as React.ReactElement}
                </Tooltip>
              );
            }}
          </Listbox.Option>
        ))}
        {hasMore ? (
          <div
            ref={loadMoreRef}
            data-testid="select-load-more-row"
            className="flex select-none items-center gap-2 px-3 py-2 text-sm text-gray-500"
          >
            <Spinner />
            {t('loading')}
          </div>
        ) : null}
      </Listbox.Options>
    </Transition>
  );

  return (
    <div
      className={cx(className, {
        'h-9': !accessibleLabel,
        'min-w-fit w-full': !inline,
        'min-w-fit w-fit': inline,
      })}
    >
      <Listbox
        value={value}
        onChange={handleChange}
        multiple={multiple}
        disabled={disabled}
      >
        {({ open }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            onOpenChange?.(open);

            if (portal && open) {
              updateDropdownPosition();
              window.addEventListener('scroll', updateDropdownPosition, true);
              window.addEventListener('resize', updateDropdownPosition);
            }
            return () => {
              if (portal) {
                window.removeEventListener(
                  'scroll',
                  updateDropdownPosition,
                  true,
                );
                window.removeEventListener('resize', updateDropdownPosition);
              }
            };
          }, [open]);

          return (
            <>
              {accessibleLabel && (
                <Listbox.Label
                  className={accessibleLabelClassName || 'sr-only'}
                >
                  {accessibleLabel}
                </Listbox.Label>
              )}
              <div
                className={cx('relative', accessibleLabel ? 'h-9' : 'h-full')}
              >
                <Listbox.Button
                  ref={buttonRef}
                  id={id}
                  className={cx(
                    'relative flex cursor-pointer border rounded-lg h-full px-2 font-medium outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    {
                      'w-full': !inline,
                      'w-fit': inline,
                      'border-neutral-b4': !hasErrors,
                      'border-red-500 ring-1 ring-red-500 focus-visible:border-neutral-b4':
                        hasErrors,
                      'bg-gray-100 text-gray-500 !cursor-not-allowed': disabled,
                    },
                    buttonClassName,
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <span
                      className={cx(
                        'text-sm whitespace-nowrap flex items-center gap-1',
                        {
                          'text-blue-500': open,
                          'text-black': !open,
                          'text-gray-400': disabled,
                        },
                      )}
                    >
                      {icon}
                      {label}
                      {multiple && selectedCount > 0 && (
                        <span className="text-xs flex items-center justify-center text-current bg-blue-500 text-white rounded-full px-2">
                          {selectedCount}
                        </span>
                      )}
                    </span>
                    <span className="pointer-events-none flex items-center pl-2">
                      <CaretDown
                        width={16}
                        height={16}
                        className={`${open ? 'rotate-180' : ''}`}
                      />
                    </span>
                  </div>
                </Listbox.Button>
                {open &&
                  (portal
                    ? createPortal(renderDropdownContent(open), document.body)
                    : renderDropdownContent(open))}
              </div>
            </>
          );
        }}
      </Listbox>
    </div>
  );
};
